import React, { useCallback, useMemo } from 'react';
import classnames from 'clsx';

import { NumberInput } from 'ui-components/components/number-input';
import { Text, TextType } from 'ui-components/components/text';

import { AnalyticsLicenseCost } from './analytics-license-cost';
import { messages } from './messages';
import styles from './plan-analytics-license-count.module.less';
import planDetailStyles from '../plan-details.module.less';
import { SectionIds } from '../../../manage-subscription-drawer/constants';
import { useManageSubscriptionDrawerContext } from '../../../../context/manage-subscription-drawer-context';
import { useGetNumberOfIncludedAnalyticsLicensesForPlan } from '../../../../hooks/use-plan-details';
import type { BillingPlan } from '../../../../../../../prop-types/subscription-data-types.d';

type PlanAnalyticsLicenseCountProps = {
  discountPercent?: number;
  onChange: (billingPlanId: string, count: number) => void;
  plan: BillingPlan;
  additionalLicenseCount: number;
};

export const PlanAnalyticsLicenseCount = ({ discountPercent, onChange, plan, additionalLicenseCount }: PlanAnalyticsLicenseCountProps) => {
  const hasAnalyticsLicenses = useMemo(() => {
    return (plan?.analyticsLicenseLimit?.limit ?? 0) > 0;
  }, [plan]);

  const { highlightSection } = useManageSubscriptionDrawerContext();
  const shouldHighlight = highlightSection === SectionIds.AnalyticsLicenses;

  const includedLicenseCount = useGetNumberOfIncludedAnalyticsLicensesForPlan(plan.billingPlanId);

  const onChangeLicenseCount = useCallback(
    (count: number | undefined) => {
      const newAdditionalLicenseCount = (count ?? includedLicenseCount) - includedLicenseCount;
      onChange(plan.billingPlanId, newAdditionalLicenseCount);
    },
    [includedLicenseCount, plan.billingPlanId, onChange],
  );

  if (!hasAnalyticsLicenses) {
    return null;
  }

  return (
    <div className={classnames(styles['plan-analytics-license-count'], { [planDetailStyles.highlight]: shouldHighlight })}>
      <Text className={styles['analytics-license-label']} textType={TextType.Label} value={messages.selectAnalyticsLicensesLabel} />
      <NumberInput
        adornment={
          <AnalyticsLicenseCost
            additionalLicenses={additionalLicenseCount}
            billingPlanId={plan.billingPlanId}
            discountPercent={discountPercent}
          />
        }
        adornmentPosition='end'
        className={styles['analytics-license-input']}
        min={includedLicenseCount}
        onChange={onChangeLicenseCount}
        value={includedLicenseCount + additionalLicenseCount}
      />
    </div>
  );
};
